import { Component, type ErrorInfo, type ReactNode } from "react";
import ExperienceFailureState from "@/components/ui/ExperienceFailureState";
import { logger } from "@/lib/logger";

type GlobeErrorBoundaryProps = {
  children: ReactNode;
  onError?: () => void;
  onRetry: () => void;
  retryKey: number;
};

type GlobeErrorBoundaryState = {
  error: Error | null;
};

class GlobeErrorBoundary extends Component<GlobeErrorBoundaryProps, GlobeErrorBoundaryState> {
  override state: GlobeErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): GlobeErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.captureError("Globe render failed", error, {
      componentStack: errorInfo.componentStack,
    });
    this.props.onError?.();
  }

  override componentDidUpdate(previousProps: GlobeErrorBoundaryProps) {
    if (previousProps.retryKey !== this.props.retryKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <ExperienceFailureState
        actionLabel="Retry Globe"
        description="The 3D globe did not initialize correctly. Retry the scene to continue the interactive experience."
        detail={import.meta.env.DEV ? this.state.error.message : null}
        onAction={this.props.onRetry}
        title="Globe Unavailable"
      />
    );
  }
}

export default GlobeErrorBoundary;
